import React from 'react';
import { LuEye, LuEyeOff } from 'react-icons/lu';
import { FormattedMessage } from 'react-intl';

import { RequiredFlag } from '../required-flag';
import usePasswordInput from './password-input.controller';

const ID = 'password-input';
export interface PasswordInputProps {
    compareMultiple?: string[];
    compare?: string;
    strength?: boolean;
    toRepeat?: string;
    required?: boolean;
    isValidCallback?: (valid: boolean) => void;
    onChange?: (value: string) => void;
    onFocusChange?: () => void;
    value?: string;
    errorMessage?: React.ReactNode;
    label?: React.ReactNode;
    helpMessage?: React.ReactNode;
    autoFocus?: boolean;
    id?: string;
    noAutoComplete?: boolean;
    name?: string;
    noConditionedStyling?: boolean;
    valid?: boolean;

    styles?: PasswordInputStyles;
}

export interface PasswordInputStyles {
    container?: string;
    label?: {
        container?: string;
        required?: string;
        label?: string;
    };
    input?: {
        outside_container?: string;
        inside_container?: string;
        icon_container?: string;
        icon?: string;
        input?: string;
    };
    error?: {
        container?: string;
        message?: string;
    };
}

export enum PasswordInputError {
    NONE,
    REQUIRED,
    STRENGTH,
    SIMILARITY,
    REPEAT_NOT_IDENTICAL
}

export const PasswordInput = (props: PasswordInputProps) => {
    const viewProps = usePasswordInput(props);

    return (
        <div
            id={`${ID}-${props.id}`}
            className={props.styles?.container ?? 'inline-flex w-full flex-col items-start justify-start gap-1'}>
            {props.label && (
                <div className='mb-0! flex w-full flex-col'>
                    <div className={props.styles?.label?.container ?? 'inline-flex items-center gap-1'}>
                        <span className={props.styles?.label?.label ?? 'text-foreground-default text-sm font-medium'}>{props.label}</span>
                        <RequiredFlag required={props.required} />
                    </div>
                    {props.helpMessage && (
                        <>
                            <small className='form-text text-muted'>{props.helpMessage}</small>
                        </>
                    )}
                </div>
            )}
            <div
                className={
                    props.styles?.input?.outside_container ??
                    'bg-background-surface outline-border-default inline-flex h-11 flex-col items-start justify-center gap-3.5 self-stretch rounded-xl px-4 outline-1 outline-offset-[-1px]'
                }>
                <div className={props.styles?.input?.inside_container ?? 'inline-flex items-center justify-start gap-2 self-stretch'}>
                    <input
                        className={
                            props.styles?.input?.input ?? 'text-foreground-default w-full justify-start self-stretch text-base font-medium focus:outline-none'
                        }
                        autoFocus={props.autoFocus}
                        data-testid='passwordInput'
                        id={props.id ? props.id + '-password-input' : 'password-input'}
                        name='no-complete-password'
                        autoComplete={props.noAutoComplete ? 'new-password' : 'current-password'}
                        value={props.value}
                        type={viewProps.visible ? 'text' : 'password'}
                        onFocus={() => {
                            viewProps.changeTouched(true);
                            props.onFocusChange && props.onFocusChange();
                        }}
                        required={props.required}
                        onChange={(event) => {
                            if (props.onChange) props.onChange(event.target.value);
                            viewProps.isValid();
                        }}
                    />
                    <button
                        className='hover:bg-background-default cursor-pointer bg-background-surface text-foreground-default rounded-full p-1.5'
                        type='button'
                        id={props.id ? props.id + '-password-show-button' : 'password-show-button'}
                        onClick={() => viewProps.changeVisible(!viewProps.visible)}>
                        {viewProps.visible ? <LuEyeOff /> : <LuEye />}
                    </button>
                </div>
            </div>
            {props.strength && (
                <>
                    <div className='h-1.5 w-full rounded-full bg-background-default'>
                        <div
                            className={'h-1.5 rounded-full transition-all delay-75 duration-150 ' + viewProps.color}
                            role='progressbar'
                            aria-valuenow={viewProps.strength}
                            aria-valuemin={0}
                            aria-valuemax={100}
                            style={viewProps.style}
                        />
                    </div>
                    <span>
                        <small>
                            <FormattedMessage
                                id='PasswordInput.StrengthHelpMessage'
                                defaultMessage={
                                    'Your password should contain at least 1 small letter, 1 capital letter, 1 number, 1 symbol and be a minimum of 8 characters long.'
                                }
                                description='Help message for password strength'
                            />
                        </small>
                    </span>
                </>
            )}
            {viewProps.valid === false && viewProps.touched === true && (
                <>
                    {props.errorMessage === undefined ? (
                        <div className={props.styles?.error?.container ?? 'inline-flex w-full flex-col items-start justify-start gap-1'}>
                            <small className={props.styles?.error?.message ?? 'text-status-danger text-xs'}>
                                {viewProps.error === PasswordInputError.REQUIRED && 'A password is required.'}
                                {viewProps.error === PasswordInputError.STRENGTH && 'Make sure your password meets all strength requirements.'}
                                {viewProps.error === PasswordInputError.SIMILARITY &&
                                    'Your password shares similarity with other information you have given. Please make it more random.'}
                                {viewProps.error === PasswordInputError.REPEAT_NOT_IDENTICAL && 'Both passwords should be the same.'}
                            </small>
                        </div>
                    ) : (
                        <div className={props.styles?.error?.container ?? 'inline-flex w-full flex-col items-start justify-start gap-1'}>
                            <small className={props.styles?.error?.message ?? 'text-status-danger text-xs'}>{props.errorMessage}</small>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};
